import React, { useState, useCallback, useRef } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import LottieView from 'react-native-web-lottie';
import { useChat } from '@livekit/components-react';
import World from 'components/World';
import { useRemScale } from 'lib/remScale';
import {
  TUTOR_INPUT_BOTTOM_REM,
  TUTOR_INPUT_SIDE_PADDING_REM,
  TUTOR_INPUT_MAX_WIDTH,
  TUTOR_INPUT_OUTER_HEIGHT_REM,
} from './tutorUiLayout';

const PROMPT_TAB_ACCENT_COLOR = '#D7F46E';
const PROMPT_TAB_LOTTIE_COLOR_FILTERS = [
  {keypath: 'Fill 1', color: PROMPT_TAB_ACCENT_COLOR},
  {keypath: 'Stroke 1', color: PROMPT_TAB_ACCENT_COLOR},
];

export default function LiveKitTextInput({
  status = 'prompt',
  showMicPermissionWarning = false,
  onMessageSent,
}) {
  const [textMessage, setTextMessage] = useState('');
  const { send, isSending } = useChat();
  const [isHoveredSend, setIsHoveredSend] = useState(false);
  const lastSendAtRef = useRef(0);
  const { rem } = useRemScale();
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
  const isCompactViewport = viewportWidth <= 1180 || viewportHeight <= 760;

  // Input only accepts typing on the user's idle turn.
  const effectiveDisabled = isSending || status !== 'prompt';

  // memoize send handler, and notify parent to disable after send
  const onSend = useCallback(() => {
    if (effectiveDisabled) return;
    const msg = textMessage.trim();
    if (!msg) return;
    // Guard against accidental double-firing (e.g. keydown repeat / duplicate events).
    const now = Date.now();
    if (now - lastSendAtRef.current < 250) return;
    lastSendAtRef.current = now;
    console.log('[LiveKitTextInput] sending (explicit)', { length: msg.length });
    send(msg);
    setTextMessage('');
    if (typeof onMessageSent === 'function') {
      onMessageSent(msg);
    }
    World.runCommand(
      `livekit_final_response dummy ${msg.replace(/ /g, "_")} true player`
    );

  }, [effectiveDisabled, textMessage, send, onMessageSent]);

  // web-only hover handlers
  const hoverProps = Platform.OS === 'web' && !effectiveDisabled
    ? {
        onMouseEnter: () => setIsHoveredSend(true),
        onMouseLeave: () => setIsHoveredSend(false),
      }
    : {};

  // Web: `onKeyPress` is unreliable in react-native-web and can fire oddly with IME/composition.
  // Use `onKeyDown` and ONLY send on explicit Enter.
  const isExplicitEnter = useCallback((e) => {
    const ne = e?.nativeEvent;
    const key = ne?.key ?? e?.key;
    const keyCode = ne?.keyCode ?? e?.keyCode ?? ne?.which ?? e?.which;
    const isEnter = key === 'Enter' || keyCode === 13;
    if (!isEnter) return false;

    const isComposing = Boolean(ne?.isComposing ?? e?.isComposing);
    if (isComposing) return false;

    const repeat = Boolean(ne?.repeat ?? e?.repeat);
    if (repeat) return false;

    const shiftKey = Boolean(ne?.shiftKey ?? e?.shiftKey);
    const ctrlKey = Boolean(ne?.ctrlKey ?? e?.ctrlKey);
    const metaKey = Boolean(ne?.metaKey ?? e?.metaKey);
    const altKey = Boolean(ne?.altKey ?? e?.altKey);
    if (shiftKey || ctrlKey || metaKey || altKey) return false;

    return true;
  }, []);

  const onKeyDown = useCallback(
    e => {
      if (effectiveDisabled) return;
      if (!isExplicitEnter(e)) return;

      e?.preventDefault?.();
      e?.stopPropagation?.();
      onSend();
    },
    [effectiveDisabled, onSend, isExplicitEnter],
  );

  // Fallback for some react-native-web builds where keydown doesn't propagate as expected.
  const onKeyPress = useCallback(
    e => {
      if (effectiveDisabled) return;
      if (!isExplicitEnter(e)) return;
      e?.preventDefault?.();
      e?.stopPropagation?.();
      onSend();
    },
    [effectiveDisabled, onSend, isExplicitEnter],
  );

  return (
    <View
      style={[
        styles.container,
        {
          bottom: rem(TUTOR_INPUT_BOTTOM_REM),
          paddingHorizontal: rem(TUTOR_INPUT_SIDE_PADDING_REM),
        },
      ]}>
      <View style={styles.inputShell}>
        {showMicPermissionWarning ? (
          <View style={styles.promptTabAnchor} pointerEvents="none">
            <View
              style={[
                styles.permissionWarning,
                isCompactViewport && styles.permissionWarningCompact,
              ]}>
              <Icon
                name="microphone-off"
                size={isCompactViewport ? 13 : 14}
                style={styles.permissionWarningIcon}
              />
              <Text
                style={[
                  styles.permissionWarningText,
                  isCompactViewport && styles.permissionWarningTextCompact,
                ]}>
                Please allow microphone access and reload this tab for the best experience.
              </Text>
            </View>
          </View>
        ) : status === 'speaking' ? (
          <View style={styles.promptTabAnchor} pointerEvents="none">
            <View style={styles.speakingTab}>
              <Icon name="account-voice" size={14} style={styles.speakingTabIcon} />
              <Text style={styles.speakingTabText}>Tutor is speaking…</Text>
            </View>
          </View>
        ) : status === 'listening' ? (
          <View style={styles.promptTabAnchor} pointerEvents="none">
            <View style={styles.listeningTab}>
              <Icon name="microphone" size={14} style={styles.listeningTabIcon} />
              <Text style={styles.listeningTabText}>Listening…</Text>
            </View>
          </View>
        ) : status === 'thinking' ? (
          <View style={styles.promptTabAnchor} pointerEvents="none">
            <View style={styles.thinkingTab}>
              <Icon name="dots-horizontal" size={14} style={styles.thinkingTabIcon} />
              <Text style={styles.thinkingTabText}>Tutor is thinking…</Text>
            </View>
          </View>
        ) : (
          <View style={styles.promptTabAnchor} pointerEvents="none">
            <View
              style={[
                styles.promptTab,
                isCompactViewport && styles.promptTabCompact,
              ]}>
              <LottieView
                source={require('../../utils/animations/mic-animation.json')}
                autoPlay
                loop
                colorFilters={PROMPT_TAB_LOTTIE_COLOR_FILTERS}
                style={[
                  styles.promptTabLottie,
                  isCompactViewport && styles.promptTabLottieCompact,
                ]}
              />
              <Text
                style={[
                  styles.promptTabText,
                  isCompactViewport && styles.promptTabTextCompact,
                ]}>
                Speak or type your response
              </Text>
            </View>
          </View>
        )}
        <View style={[styles.inputBar, {minHeight: rem(TUTOR_INPUT_OUTER_HEIGHT_REM)}]}>
          <TextInput
            // Use editable across platforms; on web we also pass `disabled` for safety.
            editable={!effectiveDisabled}
            {...(Platform.OS === 'web' ? { disabled: effectiveDisabled } : {})}
            style={styles.textInput}
            value={textMessage}
            onChangeText={setTextMessage}
            placeholder="Type here to respond..."
            placeholderTextColor="rgba(246,251,255,0.56)"
            returnKeyType="send"
            multiline={false}
            blurOnSubmit={false}
            // Prefer onSubmitEditing when available; add web key handlers as belt-and-suspenders.
            onSubmitEditing={onSend}
            onKeyDown={Platform.OS === 'web' ? onKeyDown : undefined}
            onKeyPress={Platform.OS === 'web' ? onKeyPress : undefined}
          />
          <TouchableOpacity
            style={[
              styles.sendButton,
              isHoveredSend && styles.sendButtonHover,
              effectiveDisabled && styles.sendButtonDisabled,
            ]}
            onPress={onSend}
            disabled={effectiveDisabled}
            {...hoverProps}
          >
            <Icon
              name="send-outline"
              size={16}
              style={[
                styles.sendButtonIcon,
                isHoveredSend && styles.sendButtonIconHover,
                effectiveDisabled && styles.sendButtonIconDisabled,
              ]}
            />
            <Text
              style={[
                styles.sendButtonText,
                isHoveredSend && styles.sendButtonTextHover,
                effectiveDisabled && styles.sendButtonTextDisabled,
              ]}
            >
              Send Message
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 16,
    left: 0,
    right: 0,
    zIndex: 100,
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  inputShell: {
    width: '100%',
    maxWidth: TUTOR_INPUT_MAX_WIDTH,
    position: 'relative',
  },
  promptTabAnchor: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '100%',
    alignItems: 'center',
    zIndex: 3,
  },
  promptTab: {
    maxWidth: 340,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: -1,
    backgroundColor: 'rgba(34,42,58,0.56)',
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(197,232,54,0.20)',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  promptTabCompact: {
    maxWidth: 286,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  promptTabLottie: {
    width: 18,
    height: 18,
    marginRight: 6,
  },
  promptTabLottieCompact: {
    width: 16,
    height: 16,
    marginRight: 5,
  },
  promptTabText: {
    color: PROMPT_TAB_ACCENT_COLOR,
    fontWeight: '700',
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.2,
  },
  promptTabTextCompact: {
    fontSize: 10,
    lineHeight: 13,
  },
  speakingTab: {
    maxWidth: 340,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: -1,
    backgroundColor: 'rgba(34,42,58,0.92)',
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(124,193,255,0.30)',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  speakingTabIcon: {
    color: '#7CC1FF',
    marginRight: 6,
  },
  speakingTabText: {
    color: '#BFE0FF',
    fontWeight: '700',
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.2,
  },
  listeningTab: {
    maxWidth: 340,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: -1,
    backgroundColor: 'rgba(34,42,58,0.92)',
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(95,224,138,0.35)',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  listeningTabIcon: {
    color: '#5FE08A',
    marginRight: 6,
  },
  listeningTabText: {
    color: '#C8FFDA',
    fontWeight: '700',
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.2,
  },
  thinkingTab: {
    maxWidth: 340,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: -1,
    backgroundColor: 'rgba(34,42,58,0.92)',
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(255,194,75,0.40)',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  thinkingTabIcon: {
    color: '#FFC24B',
    marginRight: 6,
  },
  thinkingTabText: {
    color: '#FFE6B8',
    fontWeight: '700',
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.2,
  },
  permissionWarning: {
    maxWidth: 500,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 7,
    marginBottom: -1,
    backgroundColor: 'rgba(76,22,20,0.92)',
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(255,142,124,0.55)',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  permissionWarningCompact: {
    maxWidth: 360,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  permissionWarningIcon: {
    color: '#FFD0C8',
    marginRight: 6,
  },
  permissionWarningText: {
    flex: 1,
    color: '#FFE5DF',
    fontWeight: '700',
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.2,
  },
  permissionWarningTextCompact: {
    fontSize: 10,
    lineHeight: 13,
  },
  inputBar: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(34,42,58,0.56)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.20)',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOpacity: 0.35,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
  },
  textInput: {
    flex: 1,
    backgroundColor: 'rgba(9,12,20,0.45)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.20)',
    borderRadius: 11,
    paddingVertical: 11,
    paddingHorizontal: 12,
    color: 'rgba(246,251,255,0.96)',
    fontSize: 15,
    ...(Platform.OS === 'web' ? { outlineWidth: 0 } : {}),
  },
  sendButton: {
    marginLeft: 10,
    paddingHorizontal: 12,
    paddingVertical: 11,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 11,
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.20)',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  sendButtonHover: {
    backgroundColor: 'rgba(197,232,54,0.92)',
    borderColor: 'rgba(197,232,54,0.92)',
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
  sendButtonIcon: {
    color: '#D7F46E',
    marginRight: 6,
  },
  sendButtonIconHover: {
    color: '#000',
  },
  sendButtonIconDisabled: {
    color: '#888',
  },
  sendButtonText: {
    color: '#D7F46E',
    fontWeight: '700',
    fontSize: 13,
  },
  sendButtonTextHover: {
    color: '#000',
  },
  sendButtonTextDisabled: {
    color: '#888',
  },
});
